/**
 * VirtualizedList Component
 *
 * A generic virtualized list component for rendering large datasets efficiently.
 * Only renders visible items, improving performance for lists with hundreds
 * or thousands of items.
 *
 * Features:
 * - Renders only visible items
 * - Fixed or variable row heights
 * - Keyboard navigation
 * - Loading and empty states
 * - Smooth scrolling
 * - Scroll position restoration
 *
 * Performance targets (Phase 4):
 * - Render 1000+ items smoothly
 * - < 16ms per frame (60fps)
 * - Memory efficient (only visible items in DOM)
 *
 * Usage:
 * <VirtualizedList
 *   items={projects}
 *   rowHeight={80}
 *   renderItem={(item, index) => <ProjectCard project={item} />}
 * />
 */

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';

/**
 * Calculate visible range of items
 */
const calculateVisibleRange = (scrollTop, containerHeight, rowHeight, totalItems, overscan = 3) => {
  const startIndex = Math.floor(scrollTop / rowHeight);
  const visibleCount = Math.ceil(containerHeight / rowHeight);

  // Add overscan items above and below viewport
  const overscanStart = Math.max(0, startIndex - overscan);
  const overscanEnd = Math.min(totalItems - 1, startIndex + visibleCount + overscan);

  return {
    start: overscanStart,
    end: overscanEnd,
    visibleStart: startIndex,
    visibleEnd: startIndex + visibleCount,
  };
};

/**
 * VirtualizedList Component
 */
const VirtualizedList = forwardRef(({
  items = [],
  rowHeight = 60,
  renderItem,
  height = 400,
  width = '100%',
  overscan = 3,
  loading = false,
  loadingComponent,
  emptyMessage = 'No items to display',
  emptyComponent,
  onScroll,
  onItemsRendered,
  initialScrollTop = 0,
  getItemKey,
  className = '',
  containerClassName = '',
  itemClassName = '',
  keyboardNavigation = true,
  ariaLabel = 'List',
}, ref) => {
  const containerRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(initialScrollTop);
  const [containerHeight, setContainerHeight] = useState(height);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Calculate total height and visible items
  const totalHeight = items.length * rowHeight;
  const visibleRange = useMemo(
    () => calculateVisibleRange(scrollTop, containerHeight, rowHeight, items.length, overscan),
    [scrollTop, containerHeight, rowHeight, items.length, overscan]
  );

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    scrollTo: (offset) => {
      if (containerRef.current) {
        containerRef.current.scrollTop = offset;
      }
    },
    scrollToItem: (index, align = 'smart') => {
      if (containerRef.current && index >= 0 && index < items.length) {
        const itemTop = index * rowHeight;
        const itemBottom = itemTop + rowHeight;
        const scrollTop = containerRef.current.scrollTop;
        const containerHeight = containerRef.current.clientHeight;

        let newScrollTop;
        switch (align) {
          case 'start':
            newScrollTop = itemTop;
            break;
          case 'end':
            newScrollTop = itemBottom - containerHeight;
            break;
          case 'center':
            newScrollTop = itemTop - (containerHeight - rowHeight) / 2;
            break;
          case 'smart':
          default:
            if (itemTop < scrollTop) {
              newScrollTop = itemTop;
            } else if (itemBottom > scrollTop + containerHeight) {
              newScrollTop = itemBottom - containerHeight;
            } else {
              return; // Already visible
            }
        }

        containerRef.current.scrollTop = Math.max(0, newScrollTop);
      }
    },
    getScrollTop: () => containerRef.current?.scrollTop || 0,
    focusItem: (index) => setFocusedIndex(index),
  }));

  // Update container height on resize
  useEffect(() => {
    if (typeof height === 'number') {
      setContainerHeight(height);
      return;
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerHeight(entry.contentRect.height);
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [height]);

  // Handle scroll
  const handleScroll = useCallback((e) => {
    const newScrollTop = e.target.scrollTop;
    setScrollTop(newScrollTop);
    onScroll?.(newScrollTop);
  }, [onScroll]);

  // Notify when rendered items change
  useEffect(() => {
    if (onItemsRendered && items.length > 0) {
      onItemsRendered({
        overscanStartIndex: visibleRange.start,
        overscanEndIndex: visibleRange.end,
        visibleStartIndex: visibleRange.visibleStart,
        visibleEndIndex: Math.min(visibleRange.visibleEnd, items.length - 1),
      });
    }
  }, [visibleRange, onItemsRendered, items.length]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!keyboardNavigation || items.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => Math.min(prev + 1, items.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Home':
        e.preventDefault();
        setFocusedIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setFocusedIndex(items.length - 1);
        break;
      case 'PageDown':
        e.preventDefault();
        setFocusedIndex((prev) =>
          Math.min(prev + Math.floor(containerHeight / rowHeight), items.length - 1)
        );
        break;
      case 'PageUp':
        e.preventDefault();
        setFocusedIndex((prev) =>
          Math.max(prev - Math.floor(containerHeight / rowHeight), 0)
        );
        break;
      default:
        break;
    }
  }, [keyboardNavigation, items.length, containerHeight, rowHeight]);

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && containerRef.current) {
      const itemTop = focusedIndex * rowHeight;
      const itemBottom = itemTop + rowHeight;
      const scrollTop = containerRef.current.scrollTop;
      const containerHeight = containerRef.current.clientHeight;

      if (itemTop < scrollTop) {
        containerRef.current.scrollTop = itemTop;
      } else if (itemBottom > scrollTop + containerHeight) {
        containerRef.current.scrollTop = itemBottom - containerHeight;
      }
    }
  }, [focusedIndex, rowHeight]);

  // Get item key
  const getKey = useCallback((item, index) => {
    if (getItemKey) return getItemKey(item, index);
    if (item.id !== undefined) return item.id;
    if (item.key !== undefined) return item.key;
    return index;
  }, [getItemKey]);

  // Loading state
  if (loading) {
    if (loadingComponent) {
      return loadingComponent;
    }
    return (
      <div
        className={`flex items-center justify-center ${className}`}
        style={{ height, width }}
        role="status"
        aria-live="polite"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400 mx-auto" />
          <p className="text-gray-400 mt-2">Loading...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (items.length === 0) {
    if (emptyComponent) {
      return emptyComponent;
    }
    return (
      <div
        className={`flex items-center justify-center text-gray-400 ${className}`}
        style={{ height, width }}
        role="status"
      >
        {emptyMessage}
      </div>
    );
  }

  // Generate visible items
  const visibleItems = [];
  for (let i = visibleRange.start; i <= visibleRange.end; i++) {
    if (i < items.length) {
      const item = items[i];
      const key = getKey(item, i);
      const isFocused = i === focusedIndex;

      visibleItems.push(
        <div
          key={key}
          className={`${itemClassName} ${isFocused ? 'ring-2 ring-primary-500 ring-inset' : ''}`}
          style={{
            position: 'absolute',
            top: i * rowHeight,
            left: 0,
            right: 0,
            height: rowHeight,
          }}
          role="option"
          aria-selected={isFocused}
          tabIndex={isFocused ? 0 : -1}
          data-index={i}
        >
          {renderItem(item, i, isFocused)}
        </div>
      );
    }
  }

  return (
    <div
      ref={containerRef}
      className={`relative overflow-auto ${containerClassName}`}
      style={{
        height,
        width,
      }}
      onScroll={handleScroll}
      onKeyDown={handleKeyDown}
      role="listbox"
      aria-label={ariaLabel}
      tabIndex={0}
    >
      <div
        className={className}
        style={{
          height: totalHeight,
          position: 'relative',
        }}
      >
        {visibleItems}
      </div>
    </div>
  );
});

VirtualizedList.displayName = 'VirtualizedList';

VirtualizedList.propTypes = {
  /** Array of items to render */
  items: PropTypes.array.isRequired,
  /** Height of each row in pixels */
  rowHeight: PropTypes.number,
  /** Function to render each item: (item, index, isFocused) => ReactNode */
  renderItem: PropTypes.func.isRequired,
  /** Height of the container */
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Width of the container */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Number of items to render outside viewport */
  overscan: PropTypes.number,
  /** Whether list is loading */
  loading: PropTypes.bool,
  /** Custom loading component */
  loadingComponent: PropTypes.node,
  /** Message to show when list is empty */
  emptyMessage: PropTypes.string,
  /** Custom empty state component */
  emptyComponent: PropTypes.node,
  /** Callback when scroll position changes */
  onScroll: PropTypes.func,
  /** Callback when rendered items change */
  onItemsRendered: PropTypes.func,
  /** Initial scroll position */
  initialScrollTop: PropTypes.number,
  /** Function to get unique key for item */
  getItemKey: PropTypes.func,
  /** CSS class for inner container */
  className: PropTypes.string,
  /** CSS class for outer container */
  containerClassName: PropTypes.string,
  /** CSS class for each item */
  itemClassName: PropTypes.string,
  /** Enable keyboard navigation */
  keyboardNavigation: PropTypes.bool,
  /** ARIA label for the list */
  ariaLabel: PropTypes.string,
};

/**
 * Hook for managing virtualized list state
 */
export const useVirtualizedList = (items, options = {}) => {
  const {
    rowHeight = 60,
    containerHeight = 400,
    overscan = 3,
    initialScrollTop = 0,
  } = options;

  const [scrollTop, setScrollTop] = useState(initialScrollTop);

  const totalHeight = items.length * rowHeight;
  const visibleRange = useMemo(
    () => calculateVisibleRange(scrollTop, containerHeight, rowHeight, items.length, overscan),
    [scrollTop, containerHeight, rowHeight, items.length, overscan]
  );

  const handleScroll = useCallback((newScrollTop) => {
    setScrollTop(newScrollTop);
  }, []);

  const scrollToItem = useCallback((index, align = 'smart') => {
    if (index < 0 || index >= items.length) return scrollTop;

    const itemTop = index * rowHeight;
    const itemBottom = itemTop + rowHeight;

    switch (align) {
      case 'start':
        return itemTop;
      case 'end':
        return itemBottom - containerHeight;
      case 'center':
        return itemTop - (containerHeight - rowHeight) / 2;
      case 'smart':
      default:
        if (itemTop < scrollTop) return itemTop;
        if (itemBottom > scrollTop + containerHeight) return itemBottom - containerHeight;
        return scrollTop;
    }
  }, [items.length, rowHeight, containerHeight, scrollTop]);

  return {
    scrollTop,
    setScrollTop,
    handleScroll,
    scrollToItem,
    totalHeight,
    visibleRange,
    visibleItems: items.slice(visibleRange.start, visibleRange.end + 1),
    visibleStartIndex: visibleRange.start,
  };
};

/**
 * Simple virtualized item component for common use cases
 */
export const VirtualizedItem = ({ children, style, className = '' }) => (
  <div
    style={style}
    className={`flex items-center px-4 ${className}`}
  >
    {children}
  </div>
);

VirtualizedItem.propTypes = {
  children: PropTypes.node,
  style: PropTypes.object,
  className: PropTypes.string,
};

export default VirtualizedList;
